import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { convertRichTextToHTML, Drawer, FullColumn, isEmptyRichTextString, useOverlayContext } from 'seagull';
import { useAcceptServiceDescription } from '~/hooks/reactQuery/mutations/useAcceptServiceDescription';
import { useGenerateServiceDescription } from '~/hooks/reactQuery/mutations/useGenerateServiceDescription';
import { GenerateServiceDescriptionType } from '~/hooks/reactQuery/mutations/useGenerateServiceDescription.types';
import { useRejectServiceDescription } from '~/hooks/reactQuery/mutations/useRejectServiceDescription';
import { useUndoServiceDescription } from '~/hooks/reactQuery/mutations/useUndoServiceDescription';
import { useLocaleKeys } from '~/i18n';
import { useIoContext } from '~/providers/IoContext';
import { useStoreActions } from '~/store/store-context';
import DrawerHeader from './DrawerHeader';
import FeedbackModal from './FeedbackModal/FeedbackModal';
import styles from './HelpMeWriteDrawer.module.scss';
import { emptyResponseData, getResponseData, GetHelpMeWriteDrawerTID } from './HelpMeWriteDrawer.utils';
import HelpMeWriteTextField from './HelpMeWriteTextField/HelpMeWriteTextField';
import SuggestedDescriptionDisplay from './SuggestedDescriptionDisplay/SuggestedDescriptionDisplay';
const HelpMeWriteDrawer = (param)=>{
    let { open, onClose, drawerWidth = 564, serviceName, serviceDescription, serviceId, onInsert } = param;
    const { HelpMeWriteDrawerTID } = useMemo(()=>GetHelpMeWriteDrawerTID(), []);
    const { t: suggestedTexts } = useLocaleKeys('helpMeWrite.suggestedDescription');
    const promptText = suggestedTexts.promptQuestion();
    const overlayContext = useOverlayContext();
    const { logBI } = useStoreActions();
    const { intercomClient } = useIoContext();
    const [textPrompt, setTextPrompt] = useState('');
    const [response, setResponse] = useState(emptyResponseData);
    const { generateDescription, isLoading: isGenerateDescriptionLoading } = useGenerateServiceDescription();
    const { undoDescription, isLoading: isUndoLoading } = useUndoServiceDescription();
    const { acceptDescription } = useAcceptServiceDescription();
    const { rejectDescription } = useRejectServiceDescription();
    const hasTriggeredRef = useRef(false);
    const hasUsedSuggestionRef = useRef(false);
    const initialHasServiceDescriptionRef = useRef(null);
    const [isInserting, setIsInserting] = useState(false);
    const { canUndo, isClarification, conversationId, description, isEmpty } = response;
    const { hasServiceDescription, htmlDescription } = useMemo(()=>({
            hasServiceDescription: !isEmptyRichTextString(serviceDescription),
            htmlDescription: convertRichTextToHTML(serviceDescription)
        }), [
        serviceDescription
    ]);
    const handleClose = ()=>{
        onClose();
        setTextPrompt('');
        setResponse(emptyResponseData);
        hasUsedSuggestionRef.current = false;
        initialHasServiceDescriptionRef.current = null;
        setIsInserting(false);
    };
    const closeAndReject = ()=>{
        const shouldShowFeedback = !isEmpty && !hasUsedSuggestionRef.current;
        logBI(17011);
        handleClose();
        if (!isEmpty) {
            rejectDescription(conversationId);
        }
        if (shouldShowFeedback) {
            overlayContext.showModal({
                biName: 'feedback_modal',
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(FeedbackModal, {
                        conversationId: conversationId,
                        onClose: dismissModal
                    });
                }
            });
        }
    };
    useEffect(()=>{
        if (open) {
            intercomClient.hideIntercom();
        } else {
            intercomClient.showIntercom();
        }
    }, [
        open,
        intercomClient
    ]);
    useEffect(()=>{
        if (!open) {
            hasTriggeredRef.current = false;
            setResponse(emptyResponseData);
            setTextPrompt('');
            initialHasServiceDescriptionRef.current = null;
            setIsInserting(false);
            return;
        }
        // Only check hasServiceDescription when drawer first opens
        if (initialHasServiceDescriptionRef.current === null) {
            initialHasServiceDescriptionRef.current = hasServiceDescription;
            if (hasServiceDescription) {
                setResponse(_object_spread_props(_object_spread({}, emptyResponseData), {
                    description: promptText
                }));
                return;
            }
        }
        if ((serviceName === null || serviceName === void 0 ? void 0 : serviceName.trim()) && !hasTriggeredRef.current) {
            hasTriggeredRef.current = true;
            generateDescription({
                requestType: GenerateServiceDescriptionType.Title,
                title: serviceName,
                serviceId
            }).then((result)=>setResponse(getResponseData({
                    response: result,
                    promptText
                })));
        }
    }, [
        open,
        serviceName,
        serviceId,
        generateDescription,
        hasServiceDescription
    ]);
    const handleGenerateDescription = async (request)=>{
        const result = await generateDescription(request);
        setResponse(getResponseData({
            response: result,
            promptText
        }));
    };
    const handleSubmit = async ()=>{
        if (!textPrompt.trim()) return;
        if (isEmpty) {
            await handleGenerateDescription({
                requestType: GenerateServiceDescriptionType.Description,
                description: htmlDescription,
                message: textPrompt,
                title: serviceName,
                type: 'message',
                serviceId
            });
            return;
        }
        await handleGenerateDescription({
            requestType: GenerateServiceDescriptionType.Message,
            message: textPrompt,
            conversationId: response.conversationId,
            type: 'message',
            serviceId
        });
    };
    const handlePresetClick = async (preset)=>{
        if (isEmpty) {
            await handleGenerateDescription({
                requestType: GenerateServiceDescriptionType.Description,
                description: htmlDescription,
                preset,
                title: serviceName,
                type: 'preset',
                serviceId
            });
            return;
        }
        await handleGenerateDescription({
            requestType: GenerateServiceDescriptionType.Preset,
            preset,
            conversationId,
            type: 'preset',
            serviceId
        });
    };
    const handleInsert = (parsedRichText)=>{
        hasUsedSuggestionRef.current = true;
        setIsInserting(true);
        onInsert === null || onInsert === void 0 ? void 0 : onInsert(parsedRichText);
        setTimeout(()=>handleClose(), 2000);
    };
    const handleAccept = (conversationId)=>{
        acceptDescription(conversationId);
    };
    const handleUndo = async ()=>{
        if (!conversationId) return;
        const response = await undoDescription(conversationId);
        setResponse(getResponseData({
            response,
            promptText
        }));
    };
    return /*#__PURE__*/ _jsx(Drawer, {
        variant: "persistent",
        minWidth: drawerWidth - 4,
        open: open,
        onClose: closeAndReject,
        onCloseButtonClick: closeAndReject,
        disableCloseButton: isInserting,
        PaperProps: _object_spread_props(_object_spread({}, {
            'data-testid': HelpMeWriteDrawerTID.Paper()
        }), {
            className: styles.drawerPaper,
            style: {
                width: drawerWidth
            }
        }),
        showDividers: false,
        header: /*#__PURE__*/ _jsx(DrawerHeader, {}),
        testId: HelpMeWriteDrawerTID.Root(),
        children: /*#__PURE__*/ _jsxs(FullColumn, {
            gap: 16,
            padding: 16,
            justifyContent: "space-between",
            testId: HelpMeWriteDrawerTID.Content(),
            children: [
                /*#__PURE__*/ _jsx(SuggestedDescriptionDisplay, {
                    hasServiceDescription: hasServiceDescription,
                    isLoading: isGenerateDescriptionLoading,
                    isUndoLoading: isUndoLoading,
                    suggestedDescription: description,
                    canUndo: canUndo,
                    conversationId: conversationId,
                    isClarification: isClarification,
                    onInsert: handleInsert,
                    onUndo: handleUndo,
                    onAccept: handleAccept,
                    isInserting: isInserting
                }),
                /*#__PURE__*/ _jsx(HelpMeWriteTextField, {
                    conversationId: conversationId,
                    value: textPrompt,
                    onChange: setTextPrompt,
                    onSubmit: handleSubmit,
                    onPresetClick: handlePresetClick,
                    isLoading: isGenerateDescriptionLoading || !description,
                    disabled: isGenerateDescriptionLoading || !description || isInserting,
                    isUndoLoading: isUndoLoading
                })
            ]
        })
    });
};
export default HelpMeWriteDrawer;
